import { PanelType } from '@/types/pulse';

/**
 * Decodes URL query parameters, supporting base64-encoded URL params
 * via the `data` parameter.
 *
 * The encoded value is a standard URL query string (key=value&key2=value2),
 * base64-encoded to hide values from casual users.
 */
export function decodeQueryParams(
  searchParams: Record<string, string>
): Record<string, string> {
  if (!('data' in searchParams)) return searchParams;

  try {
    const decodedString = atob(searchParams.data);
    const decoded = Object.fromEntries(new URLSearchParams(decodedString));
    const { data, ...rest } = searchParams;
    return { ...decoded, ...rest };
  } catch {
    return searchParams;
  }
}

/**
 * Encodes a params object as a base64 string for use in the `data` query parameter.
 */
export function encodeQueryParams(params: Record<string, string>): string {
  return btoa(new URLSearchParams(params).toString());
}

export interface PanelistIdMatch {
  panelistId: string;
  /** The respondents.panel value (a numeric PanelType). */
  panel: number;
}

/**
 * Resolves a respondent identity from interview query params: the first
 * matching userid param (case-insensitive) supplies the panelist id, and the
 * param name maps to a respondents.panel value. pulseiq-client owns respondent
 * resolution — the LiveKit room-creation route resolves and hands respondent_id
 * to the agent, and interview-open tracking resolves the same way — so both land
 * on the same (panel, panelist_id, workspace_id) row.
 *
 * This is the only place a respondent's panel is decided, and callers must pass
 * the value on unchanged. In particular a participant_id person is always
 * PanelType.RESPONDENT — never narrowed to the B2B/B2C sub-panels from the
 * campaign behind their link, which would split one human across two rows (see
 * the invariant on those enum members).
 */
export function getPanelistIdMatch(
  queryParams: Record<string, unknown>
): PanelistIdMatch | null {
  const lower: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(queryParams)) lower[k.toLowerCase()] = v;

  if (lower['prolific_pid'] != null) {
    return {
      panelistId: String(lower['prolific_pid']),
      panel: PanelType.PROLIFIC
    };
  }
  if (lower['participant_id'] != null) {
    return {
      panelistId: String(lower['participant_id']),
      panel: PanelType.RESPONDENT
    };
  }
  if (lower['uid'] != null) {
    return { panelistId: String(lower['uid']), panel: PanelType.CUSTOM };
  }
  return null;
}
